import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import noop from 'lodash/noop';
import ModalOverlay from '@web-chat-core/ui-kit/core/ModalOverlay';
import bgDark from '@web-chat-core/ui-kit/assets/icons/icLoginBgDark.svg';
import bgLight from '@web-chat-core/ui-kit/assets/icons/icLoginBgLight.svg';
import Typography from '../Typography';
import { useTheme } from '../../theme';
import {
    AppIconWrapper,
    AppText,
    DeepDialog,
    DialogBody,
    DialogButton,
    DialogHeader,
    DialogWrapper,
} from './FallbackModal.view';

interface FallbackModalProps {
    onDownload?: () => void;
    onTryToOpenInDesktop?: () => void;
    onContinue?: () => void;
    logoElement?: ReactNode;
    isMobile?: boolean;
}

export const FallbackModal = ({
    isMobile,
    onDownload,
    onContinue,
    logoElement,
    onTryToOpenInDesktop,
}: FallbackModalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const background = (theme.isDark ? bgDark : bgLight) as unknown as string;

    const defaultLogoPath = !logoElement ? theme.logo.main : undefined;

    return (
        <DialogWrapper background={background}>
            {!isMobile && (
                <DialogHeader>
                    {onDownload && (
                        <DialogButton
                            variant="primary"
                            size="medium"
                            onClick={onDownload}
                        >
                            {t('web_deeplink_download_app')}
                        </DialogButton>
                    )}
                </DialogHeader>
            )}
            <ModalOverlay
                onClick={noop}
                name="fallback"
                isNeedOverlay={false}
            >
                <DeepDialog>
                    {defaultLogoPath && <AppIconWrapper src={defaultLogoPath} />}
                    {logoElement}

                    <AppText variant="title6">{t('web_deeplink_choose_open_link_method')}</AppText>

                    <DialogBody>
                        <Typography
                            variant="subtitle4"
                            colorVariant="second"
                        >
                            {t('web_deeplink_auto_apply_choice')}
                        </Typography>

                        {onContinue && (
                            <DialogButton
                                variant="secondary"
                                size="medium"
                                onClick={onContinue}
                            >
                                {t('web_deeplink_open_in_browser')}
                            </DialogButton>
                        )}

                        {onTryToOpenInDesktop && !isMobile && (
                            <DialogButton
                                variant="primary"
                                size="medium"
                                onClick={onTryToOpenInDesktop}
                            >
                                {t('web_deeplink_cross_search_app_in_browser')}
                            </DialogButton>
                        )}

                        {onDownload && isMobile && (
                            <DialogButton
                                variant="primary"
                                size="medium"
                                onClick={onDownload}
                            >
                                {t('web_deeplink_download_app')}
                            </DialogButton>
                        )}
                    </DialogBody>
                </DeepDialog>
            </ModalOverlay>
        </DialogWrapper>
    );
};
